// @flow

import React, { PureComponent } from 'react';
import qs from 'qs';
import debounce from 'lodash.debounce';
import { Map } from 'immutable';
import { graphql, QueryRenderer } from 'react-relay';
import { i18n, Analytics } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Page from 'main-app/components/Page';
import PageHeaderList from 'main-app/components/PageHeaderList';
import AddUpdateInvoiceModal from 'main-app/components/AddUpdateInvoiceModal';
import FilterControls, { type FilterValue } from 'main-app/components/FilterControls';
import type { RouterHistory } from 'react-router';
import getSortOptions from './getSortOptions';
import getFilterOptions from './getFilterOptions';
import PaginatedInvoicesContainer from './PaginatedInvoicesContainer';

type Props = { history: RouterHistory };

type State = {
  addModalOpen: boolean,
  search: string,
  graphqlSearchValue: string,
  createAnotherInvoice: boolean,
};

const DEFAULT_FILTER = {
  sort: 'CREATED_AT',
  direction: 'DESC',
};

class Invoices extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      addModalOpen: false,
      search: '',
      graphqlSearchValue: '',
      createAnotherInvoice: false,
    };
    this.handleDebouncedSearch = debounce(this.handleDebouncedSearch, 500);
  }

  getFilters = (): FilterValue => {
    const { history } = this.props;
    const { search } = history.location;
    if (!search) return DEFAULT_FILTER;
    return { ...DEFAULT_FILTER, ...qs.parse(search, { ignoreQueryPrefix: true }) };
  };

  setFilters = (filters: Object) => {
    const { history } = this.props;
    history.replace(
      '/invoices?' + qs.stringify(filters, { encode: true, arrayFormat: 'brackets' }),
    );
  };

  filtersDirty = () => {
    const { graphqlSearchValue } = this.state;
    return !Map(this.getFilters()).equals(Map(DEFAULT_FILTER)) || graphqlSearchValue;
  };

  compileQueryVariables = () => {
    const filters = this.getFilters();
    const { graphqlSearchValue } = this.state;
    const queryVariables = {
      ...filters,
      search: graphqlSearchValue.trim() || null,
      sortBy: { field: filters.sort, direction: filters.direction },
    };

    if (queryVariables.issueDate) {
      const [start, end] = queryVariables.issueDate.split(' - ');
      queryVariables.issueDateStart = start;
      queryVariables.issueDateEnd = end;
      delete queryVariables.issueDate;
    }

    if (queryVariables.dueDate) {
      const [start, end] = queryVariables.dueDate.split(' - ');
      queryVariables.dueDateStart = start;
      queryVariables.dueDateEnd = end;
      delete queryVariables.dueDate;
    }

    delete queryVariables.sort;
    delete queryVariables.direction;
    return queryVariables;
  };

  handleSearchInput = (search: string) => {
    this.setState({ search }, this.handleDebouncedSearch);
  };

  handleDebouncedSearch = () => {
    const { search } = this.state;
    this.setState({ graphqlSearchValue: search });
    Analytics.trackEvent('Search Applied', {
      listName: 'Invoices',
      searchString: search,
    });
  };

  handleOpenCreateModal = () => {
    this.setState({ addModalOpen: true });
  };

  handleCloseAllModals = () => {
    this.setState({ addModalOpen: false, createAnotherInvoice: false });
  };

  handleAddModalReopen = () => {
    this.setState(
      { addModalOpen: false, createAnotherInvoice: true },
      () => {
        this.setState({ addModalOpen: true });
      },
    );
  };

  render() {
    const { history } = this.props;
    const { search, addModalOpen, createAnotherInvoice } = this.state;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        cacheConfig={{ force: true }}
        query={graphql`
          query InvoicesQuery(
            $count: Int!
            $cursor: String
            $search: String
            $sortBy: InvoiceSort
            $statuses: [InvoiceStatus!]
            $customerIds: [ID!]
            $orderIds: [ID!]
            $issueDateStart: Date
            $issueDateEnd: Date
            $dueDateStart: Date
            $dueDateEnd: Date
          ) {
            ...PaginatedInvoicesContainer_invoices
              @arguments(
                search: $search
                sortBy: $sortBy
                statuses: $statuses
                customerIds: $customerIds
                orderIds: $orderIds
                issueDateStart: $issueDateStart
                issueDateEnd: $issueDateEnd
                dueDateStart: $dueDateStart
                dueDateEnd: $dueDateEnd
              )
            viewer {
              id
              permissions
              company {
                id
              }
            }
          }
        `}
        variables={{ ...this.compileQueryVariables(), count: 30, cursor: null }}
        render={({ props }) => (
          <Page>
            <PageHeaderList
              title={i18n.t('Invoices')}
              createButtonLabel={
                props &&
                props.viewer.permissions.includes('CREATE_INVOICE') &&
                i18n.t('New Invoice')
              }
              onCreateButtonClick={this.handleOpenCreateModal}
              searchPlaceholder={i18n.t('Customer, Order #, or Invoice #')}
              searchValue={search}
              onSearchChange={this.handleSearchInput}
            />
            <FilterControls
              value={this.getFilters()}
              defaultValue={DEFAULT_FILTER}
              sortOptions={getSortOptions()}
              filterOptions={getFilterOptions()}
              savedFilterType="INVOICE"
              onFiltersChange={filters => this.setFilters(filters)}
            />
            {!props ? (
              <Loader />
            ) : (
              <PaginatedInvoicesContainer
                history={history}
                filtersDirty={this.filtersDirty()}
                onCreateNew={this.handleOpenCreateModal}
                invoices={props}
                deleteEnabled={props.viewer.permissions.includes('DELETE_INVOICE')}
                editEnabled={props.viewer.permissions.includes('UPDATE_INVOICE')}
              />
            )}
            {addModalOpen && (
              <AddUpdateInvoiceModal
                onClose={this.handleCloseAllModals}
                onSuccess={invoice => history.push(`/invoice/${invoice.id}`)}
                onReopen={this.handleAddModalReopen}
                createAnotherInvoice={createAnotherInvoice}
                invoice={null}
              />
            )}
          </Page>
        )}
      />
    );
  }
}

export default Invoices;
